import api from './api'

// AI Coaching API client. Like the learning client, these routes are
// authentication-gated only (coaching belongs to the learner), so they work for
// org-less learners as well as workforce users. The backend never returns the
// internal coaching persona/prompt.

export interface CoachGoal {
  id: string
  title: string
  goalType: 'language_fluency' | 'certification' | 'role_readiness' | 'mentor' | 'skill' | 'custom'
  targetSkill: string | null
  targetSkillId?: string | null
  targetCertification: string | null
  status: 'active' | 'achieved' | 'paused' | 'abandoned'
  priority: number
  targetDate: string | null
}

export interface CoachContext {
  learnerName: string
  streak: { currentStreakDays: number; longestStreakDays: number; lastActivityOn: string | null }
  momentum: string
  activeGoal: CoachGoal | null
  goals: CoachGoal[]
  currentCertification: { title: string; progress: number } | null
  weakAreas: string[]
  strongAreas: string[]
  recommendedLesson: Record<string, unknown> | null
  completedLessons: number
  memory: {
    lastTopic: string | null
    currentCertification: string | null
    preferredLearningStyle: string | null
    recentStruggles: string[]
    recentSuccesses: string[]
  }
}

export interface LearningPlanWeek {
  week: number
  focus: string[]
  targetConcepts: string[]
  estimatedMinutes: number
}

export interface LearningPlan {
  skillName: string
  certificationTitle: string | null
  certificationProgress: number
  alignedGoal: string | null
  weeks: LearningPlanWeek[]
}

export interface DailyBrief {
  greeting: string
  highlights: string[]
  weakestArea: string | null
  todaysFocus: { skill: string; concept: string | null; activityType: string; difficulty: string; estimatedMinutes: number } | null
  certification: { title: string; progress: number } | null
  message: string
}

export interface WeeklyReview {
  timeStudiedMinutes: number
  timeStudiedLabel: string
  competenciesImproved: string[]
  newCertifications: string[]
  weakAreas: string[]
  strongAreas: string[]
  recommendedNextWeek: LearningPlanWeek[]
  message: string
}

export interface WorkforceRecommendation {
  reason: string
  focusSkill: string | null
  focusConcept: string | null
  relatedJob: string | null
  organizationName: string | null
  suggestedAction: string
  startsAt: string | null
}

export interface CoachAskResult {
  success: boolean
  conversationId: string
  intent: string
  reply: string
  focusConcept: string | null
  suggestedActions: string[]
  source: 'deterministic' | 'llm'
}

export interface CoachConversation {
  id: string
  title: string | null
  lastMessageAt: string
  createdAt: string
  lastMessage: string | null
}

export interface CoachMessage {
  id: string
  role: 'learner' | 'coach'
  content: string
  intent: string | null
  createdAt: string
}

export interface GoalPayload {
  title: string
  goalType?: CoachGoal['goalType']
  targetSkill?: string | null
  targetSkillId?: string
  targetCertification?: string | null
  priority?: number
  targetDate?: string | null
}

export const coachingService = {
  getContext: () => api.get<{ success: boolean; context: CoachContext }>('/api/learning/coach/context'),
  getPlan: () => api.get<{ success: boolean; plan: { plans: LearningPlan[]; primaryGoal: string | null } }>('/api/learning/coach/plan'),
  getBrief: () => api.get<{ success: boolean; brief: DailyBrief }>('/api/learning/coach/brief'),
  getWeeklyReview: () => api.get<{ success: boolean; review: WeeklyReview }>('/api/learning/coach/weekly-review'),
  getWorkforceRecommendations: () =>
    api.get<{ success: boolean; recommendations: WorkforceRecommendation[] }>('/api/learning/coach/workforce'),
  ask: (message: string, conversationId?: string) =>
    api.post<CoachAskResult>('/api/learning/coach/ask', { message, conversationId }),
  listConversations: () => api.get<{ success: boolean; conversations: CoachConversation[] }>('/api/learning/coach/conversations'),
  getConversation: (conversationId: string) =>
    api.get<{ success: boolean; messages: CoachMessage[] }>(`/api/learning/coach/conversations/${conversationId}`),
  listGoals: () => api.get<{ success: boolean; goals: CoachGoal[] }>('/api/learning/coach/goals'),
  createGoal: (payload: GoalPayload) => api.post<{ success: boolean; goal: CoachGoal; reused:boolean }>('/api/learning/coach/goals', payload),
  updateGoal: (goalId: string, patch: Partial<Pick<CoachGoal, 'status' | 'priority' | 'targetDate' | 'title'>>) =>
    api.patch<{ success: boolean; goal: CoachGoal }>(`/api/learning/coach/goals/${goalId}`, patch),
}
